// Every balance knob of the kart game in one place: how long a race is, how
// hard the computer drives, what the boxes hand out, what a boost is worth.
// The values are starting points, to be tuned from the balance runs
// (balance.test.ts) and from playtests. Physics — gravity, bumps, the road —
// stays in sim.ts and tracks.ts.
//
// In debug mode, a JSON object saved in localStorage under `kartTuning` is
// merged over this at the start of each race, so a playtest can be retuned on
// the phone without a rebuild: e.g. {"laps": 3, "rubber": {"deadZone": 40}}.

export const TUNING = {
  /** Laps to a race: two keeps it to about two minutes. */
  laps: 2,

  /** Boosts, by where they came from: how long, and how much faster than top speed. */
  boost: {
    pad: { time: 1.1, mul: 1.45 },
    /** An energy drink or a Jägermeister. */
    item: { time: 1.65, mul: 1.55 },
    /** Landing a jump off a branch or a ramp without a trick: a little something. */
    landing: { time: 0.15, mul: 1.3 },
    /** Timing the start right. */
    start: { time: 1, mul: 1.35 },
  },

  /** A start boost: touch down in the last `early` seconds of the countdown. Any earlier, and you stall for `stall` seconds after GO. */
  start: { early: 1, stall: 0.6 },

  /**
   * The computer drivers: top speed as a share of yours, how wobbly their
   * line is, how long they sit on an item, and the rubber band — how much
   * quicker they get behind you, and how much slower well ahead of you.
   * From the balance runs (with drifting, tricks and the rest): on medium a
   * clean driver wins about 60% with the pack close behind, and a wobbly
   * one with the steering assist rarely comes last; on hard a clean driver
   * wins about 30%; on easy most races are won, with company. `shortcut`:
   * how often they go for a shortcut, a lap.
   */
  difficulty: {
    easy: { speed: 0.88, noise: 0.35, itemDelay: [3, 7], catchUp: 0.18, slowDown: 0.12, driftTier: 0, trick: 0.3, shortcut: 0 },
    medium: { speed: 0.93, noise: 0.15, itemDelay: [1.2, 3.5], catchUp: 0.16, slowDown: 0.08, driftTier: 1, trick: 0.6, shortcut: 0.15 },
    hard: { speed: 0.97, noise: 0.03, itemDelay: [0.3, 1.2], catchUp: 0.1, slowDown: 0.04, driftTier: 2, trick: 0.85, shortcut: 0.35 },
  },

  /**
   * Drifting. Hold (near) full lock for `enterHold` seconds at `minSpeed` of
   * top speed or more and the kart commits to a slide that way: steering
   * then only sets how tight it is, `turnMin` to `turnMax` times a normal
   * turn, for `speedLoss` of top speed. The longer you hold it, the higher
   * the tier — the sparks' colour — and letting go (steering back under
   * `releaseSteer` for `releaseHold` seconds) fires that tier's boost.
   * Scraping a wall, or a twirl, throws the charge away.
   */
  drift: {
    enterSteer: 0.9,
    enterHold: 0.12,
    minSpeed: 0.55,
    releaseSteer: 0.12,
    releaseHold: 0.08,
    turnMin: 0.3,
    turnMax: 1.1,
    /**
     * The slide, as in the phone kart racers: the nose swings into the
     * corner but the kart carries on outward, wider than it points — `slide`
     * of each turn goes into a sideways drift that dies at `slideDecay` a
     * second — and the arc tightens over the first `ramp` seconds, from
     * `rampFrom` of its full turn, rather than snapping to it at the start
     * (which pointed you at the inside wall).
     */
    slide: 0.55,
    slideDecay: 4.5,
    ramp: 0.35,
    rampFrom: 0.55,
    speedLoss: 0,
    tiers: [
      { charge: 0.55, time: 0.6, mul: 1.3 },
      { charge: 1.25, time: 1, mul: 1.35 },
      { charge: 2.1, time: 1.4, mul: 1.4 },
    ],
  },

  /**
   * A trick: flick up within `window` seconds of leaving a ramp's lip or a
   * branch, and landing it is worth `boost`. (The computer drivers pull one
   * off at their difficulty's `trick` rate.)
   */
  trick: { window: 0.35, swipePx: 35, boost: { time: 0.6, mul: 1.35 } },

  /**
   * The assists, each of which the player can turn off: steering that eases
   * you off a wall you're within `wallMargin` of and heading into, by up to
   * `strength` of full lock; drifting that starts at `driftSteer` of full
   * lock but never charges past tier `driftTier`; and items fired for you.
   */
  assist: { wallMargin: 2.5, strength: 0.45, driftSteer: 0.6, driftTier: 1 },

  /**
   * Slipstream: tucked in within `range` units behind another kart, lined up
   * with it (`cone`: how straight), for `charge` seconds, and you're pulled
   * past with a little boost.
   */
  slipstream: { range: 7, cone: 0.95, charge: 1, boost: { time: 1, mul: 1.12 } },

  /** A lob: beer or bearnaise thrown this far ahead instead of dropped, and how long before its thrower can drive into it. */
  lob: { ahead: 18, grace: 1.2 },

  /** The computer drivers' drifts: how hard they have to be steering to count as drifting, and how often one comes to nothing. Their best tier is per difficulty. */
  ai: { driftSteer: 0.5, driftRelease: 0.3, driftSkip: 0.25 },

  /**
   * The rubber band, in units of road (a kart at top speed covers 24 a
   * second): nothing within `deadZone` of you, full strength from `fullAt`,
   * never faster than `maxTop` of your top speed, and at half strength over
   * the last `finalFade` of the final lap, so the finish is down to driving.
   * Catching up is for keeping a leader company: with you third or worse,
   * the karts behind you catch up at only `trailing` of full strength —
   * no piling past someone who's struggling.
   */
  rubber: { deadZone: 25, fullAt: 80, maxTop: 1.08, finalFade: 0.2, trailing: 0.35 },

  /** The rival — the kart just ahead of you on the grid: a little quicker, quicker with items, and it wants to stay `targetAhead` units in front of you. */
  rival: { speed: 1.03, itemDelay: 0.5, targetAhead: 15 },

  items: {
    /**
     * How far back a kart is, 0 (leading) to 1: `rankWeight` of it from its
     * place, the rest from its distance behind the leader, full at `gapFull`
     * units — so fifth right on the leader's tail isn't fifth half a lap down.
     */
    rankWeight: 0.35,
    gapFull: 150,
    /** How long a box's item spins in the slot, and how long till the box is back. */
    rollTime: 0.9,
    boxRespawn: 2.5,
    /** Each item's chances [in the lead, far behind], blended by how far back you are. */
    weights: {
      beer: [25, 0],
      barf: [12, 0],
      bearnaise: [12, 2],
      smoke: [15, 0],
      glove: [25, 10],
      tuborg: [10, 14],
      soundboks: [6, 12],
      energy: [6, 18],
      jager: [4, 12],
      mosquito: [0, 26],
      /** Only once you're past `cartFrom` back. */
      cart: [0, 14],
      /** Only for whoever's last, and past `mooseFrom` back. */
      moose: [0, 4],
      /** Only as `rocket` allows. */
      rocket: [0, 6],
    } as Record<string, [number, number]>,
    cartFrom: 0.15,
    mooseFrom: 0.6,
  },

  /**
   * The New Year's rocket: it flies up the road after whoever's leading and
   * bursts over them, twirling everyone within `radius`. Only for a kart in
   * `minPlace` or worse and `minGap` units behind the leader, one in the air
   * at a time, and not again for `cooldown` seconds. The Soundboks is the
   * answer: its beats knock anything flying out of the air.
   */
  rocket: { minPlace: 4, minGap: 80, cooldown: 20, radius: 4, speed: 40 },

  /**
   * The jackpot: now and then all three reels of a box land on the same
   * item — `oddsFront` a box in the lead up to `oddsBack` far behind, times
   * `aiOdds` for a computer driver, at most once a race. For `time` seconds
   * after the reels stop, nothing can hurt you and the item never runs out:
   * use it again every `fireCooldown` seconds (a six-pack every
   * `tuborgCooldown`). Without it, `nearMiss` of the time two reels match.
   */
  frenzy: {
    oddsFront: 0.015,
    oddsBack: 0.05,
    aiOdds: 0.5,
    time: 6,
    fireCooldown: 0.35,
    tuborgCooldown: 1,
    nearMiss: 0.25,
    items: ["energy", "jager", "glove", "beer", "bearnaise", "tuborg"],
  },

  /** A twirl: how long it lasts, and how long after it you can't be hit again. */
  spin: { time: 1.3, safe: 1 },

  /** What's left of your speed running into a duck, a skier, a snowman or a kubb. */
  knock: 0.45,

  /** Hitting a wall: what a head-on hit costs, and the drag of scraping along it (per second). */
  wall: { impact: 0.35, scrape: 1.5 },

  /** The moose: how much faster than a kart, how close behind the leader it turns back (units), and how long it can last. */
  /** A croquet ball: seconds to cross the road (it waits 2.4 s off it), slow enough to see a gap in. */
  ball: { cross: 2.6 },
  moose: { speed: 1.7, catchGap: 25, time: 30 },
  /** The shopping trolley: how much faster than a kart, and for how long. */
  cart: { speed: 1.35, time: 7 },

  /**
   * Points, for the player: for each thing done, times the combo — which
   * grows by `combo.step` with each thing done in a row, up to
   * `combo.maxMult`, and is lost if `combo.window` seconds pass with nothing
   * done, on a twirl, or on scraping a wall `combo.scrape` seconds. Then a
   * bonus for where you finish, and for beating your rival — not multiplied.
   */
  score: {
    drift: [50, 100, 200],
    trick: 80,
    /** Twirling someone — and slowing them, with a swarm, sick or bearnaise. */
    hit: 150,
    slow: 100,
    box: 20,
    pad: 20,
    pass: 40,
    /** Overtaking the same kart again counts only after this many seconds. */
    passAgain: 5,
    slip: 60,
    finish: [3000, 2400, 2000, 1700, 1400, 1200, 1000, 800],
    rival: 500,
    /**
     * Every point is worth more on a harder difficulty, so the record for a
     * track can only be held by someone who played it on the hardest. Skilled
     * bots score about the same raw on all three — on hard a little less, for
     * a tougher field takes the finish bonus — so these leave the median hard
     * run 9% or more ahead of the median medium, and that of the easy runs
     * further still (`KART_BALANCE=1`).
     */
    difficultyMult: { easy: 1, medium: 1.2, hard: 1.5 },
  },
  combo: { window: 2.5, step: 0.1, maxMult: 2.5, scrape: 0.3 },
  /** Points for one to five stars on a track. */
  stars: [2500, 4000, 5500, 7000, 8500],

  /** What you see and feel: wider view on a boost, the camera's shake on a hit and a landing, and how near a threat must be to warn you (units). */
  juice: { boostFov: 0.12, shakeHit: 0.35, shakeLand: 0.12, shakeDecay: 8, warnRange: 30 },
};

type Tree = Record<string, unknown>;

const merge = (into: Tree, from: Tree) => {
  for (const [key, value] of Object.entries(from)) {
    const here = into[key];
    if (value && typeof value === "object" && !Array.isArray(value) && here && typeof here === "object") {
      merge(here as Tree, value as Tree);
    } else into[key] = value;
  }
};

/** Debug mode's override from localStorage, merged in; a broken one is ignored. */
export function applyTuningOverride() {
  try {
    const saved = localStorage.getItem("kartTuning");
    if (saved) merge(TUNING as unknown as Tree, JSON.parse(saved));
  } catch {
    // Not JSON, or no storage: play on as tuned.
  }
}
